import { useState } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { ajaxExceptionMessage } from '@skunexus/utils';
import { actions as notification } from '@skunexus/notification';
import * as API from '../api';
import withSettingsTabsWrapper from '../../../../modules/settings/components/withSettingsTabsWrapper';

const mapStateToProps = () => ({});

const mapDispatchToProps = {
    successNotification: notification.success,
    errorNotification: notification.error,
};

export const withCustomUserGridHoc = (app) => (Component) => {
    function WrappedComponent(props) {
        const { errorNotification = () => {}, successNotification = () => {} } = props;
        const [isPending, setPending] = useState(false);

        const handleChangeStatus = async ({ id, customValues: { status } = {} }, refreshData) => {
            setPending(true);
            try {
                await API.changeStatus({
                    userId: id,
                    data: [
                        {
                            custom_field_id: 'status',
                            value: status === 'Active' ? 'Inactive' : 'Active',
                        },
                    ],
                });
                successNotification('User status has changed.');
                refreshData();
            } catch (error) {
                errorNotification(ajaxExceptionMessage(error));
            } finally {
                setPending(false);
            }
        };

        const handleMassChangeStatus = async (ids, refreshData) => {
            setPending(true);
            try {
                await API.bulkChangeStatus({ data: ids });
                successNotification('User statuses has changed.');
                refreshData();
            } catch (error) {
                errorNotification(ajaxExceptionMessage(error));
            } finally {
                setPending(false);
            }
        };

        const handleMassActivate = async (ids, refreshData) => {
            await handleMassChangeStatus(
                ids
                    .filter(({ customValues: { status } = {} }) => status !== 'Active')
                    .map(({ id }) => ({
                        userId: id,
                        data: [
                            {
                                custom_field_id: 'status',
                                value: 'Active',
                            },
                        ],
                    })),
                refreshData,
            );
        };

        const handleMassDeactivate = async (ids, refreshData) => {
            await handleMassChangeStatus(
                ids
                    .filter(({ customValues: { status } = {} }) => status === 'Active')
                    .map(({ id }) => ({
                        userId: id,
                        data: [
                            {
                                custom_field_id: 'status',
                                value: 'Inactive',
                            },
                        ],
                    })),
                refreshData,
            );
        };

        const customGridData = (gridProps, props) => {
            const { permissions = {}, refreshData = () => {} } = props;
            const { canEdit = false } = permissions;

            const rowOptions = [...(gridProps?.config?.rowOptions || [])];
            rowOptions.splice(2, 0, {
                id: 'toggleStatus',
                icon: 'settings',
                label: (props) => (get(props, 'data.customValues.status') === 'Active' ? 'Deactivate' : 'Activate'),
                handler: (row) => handleChangeStatus(row, refreshData),
                isDisabled: (props) => {
                    const status = get(props, 'data.customValues.status');
                    if (status === 'Active' && !canEdit) return true;
                    if (status !== 'Active' && !canEdit) return true;
                    return false;
                },
            });

            return {
                ...gridProps,
                config: {
                    ...gridProps?.config,
                    isLoading: gridProps?.config?.isLoading || isPending,
                    rowOptions,
                    multiActionsOptions: [
                        ...(gridProps?.config?.multiActionsOptions || []),
                        ...(canEdit
                            ? [
                                  {
                                      id: 'activate',
                                      label: 'Activate',
                                      handler: (rows) => handleMassActivate(rows, refreshData),
                                      minItems: 1,
                                  },
                                  {
                                      id: 'deactivate',
                                      label: 'Deactivate',
                                      handler: (rows) => handleMassDeactivate(rows, refreshData),
                                      minItems: 1,
                                  },
                              ]
                            : []),
                    ],
                },
            };
        };

        return <Component {...props} customGridData={customGridData} />;
    }

    return withSettingsTabsWrapper(app)(connect(mapStateToProps, mapDispatchToProps)(WrappedComponent));
};
